import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/index.js';
import { API_BASE_URL } from '../config.js';
import CoachSessionAssignments from './CoachSessionAssignments.jsx';
import CheckinSummary from './CheckinSummary.jsx';
import './CoachToday.css';

// The daily-driver home for coaches & admins: today's classes with one-tap
// attendance, plus live alerts that link out to the screens that need action.
const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const fmtTime = (t) => {
  if (!t) return '';
  const [h, m] = String(t).split(':');
  const hh = ((Number(h) + 11) % 12) + 1;
  return `${hh}:${m}${Number(h) < 12 ? 'am' : 'pm'}`;
};

function CoachToday({ user: userProp }) {
  const navigate = useNavigate();
  const user = userProp || (() => { try { return JSON.parse(localStorage.getItem('ams_user') || localStorage.getItem('user') || '{}'); } catch { return {}; } })();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const res = await api.get(`${API_BASE_URL}/api/coach/today`);
      setData(res.data);
    } catch (err) {
      setData({ classes: [], alerts: {}, weekday: '' });
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  if (loading) return <div className="ct-wrap"><div className="ct-loading">Loading your day…</div></div>;

  const { classes = [], alerts = {}, weekday, wellness_flags = [], checkin_flags = [], checkin_compliance = null } = data || {};
  const firstName = (user?.name || '').split(' ')[0] || 'Coach';

  // Short human reason for a wellness flag — absolute concern, else a drop from
  // the athlete's own baseline ("▼ vs usual").
  const wellnessReason = (w) => {
    const parts = [];
    if (w.readiness_score != null && w.readiness_score < 50) {
      parts.push(`readiness ${Math.round(w.readiness_score)}`);
    } else if (w.avg_readiness != null && w.readiness_score != null && w.readiness_score <= w.avg_readiness - 15) {
      parts.push(`readiness ${Math.round(w.readiness_score)} (▼${Math.round(w.avg_readiness - w.readiness_score)} vs usual)`);
    }
    if (w.soreness >= 4) parts.push(w.soreness >= 5 ? 'very sore' : 'sore');
    else if (w.avg_soreness != null && w.soreness != null && w.soreness >= w.avg_soreness + 1.5) parts.push('sorer than usual');
    if (w.energy != null && w.energy <= 2) parts.push('low energy');
    else if (w.avg_energy != null && w.energy != null && w.energy <= w.avg_energy - 1.5) parts.push('lower energy than usual');
    return parts.join(' · ') || 'flagged';
  };
  const dateLabel = new Date().toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' });

  const alertItems = [
    { key: 'waivers', n: alerts.unsigned_waivers, label: 'unsigned waiver', to: '/waivers', cls: 'bad' },
    { key: 'unpaid', n: alerts.unpaid_invoices, label: 'unpaid invoice', to: '/invoices', cls: 'bad' },
    { key: 'insurance', n: alerts.expiring_insurance, label: 'insurance expiring', to: '/insurance', cls: 'warn' },
    { key: 'messages', n: alerts.unread_messages, label: 'unread message', to: '/communication', cls: 'info' },
  ].filter(a => a.n > 0);

  return (
    <div className="ct-wrap">
      <div className="ct-head">
        <h1>👋 Hi {firstName}</h1>
        <span className="ct-date">{dateLabel}</span>
      </div>

      {/* Alert strip */}
      {alertItems.length > 0 ? (
        <div className="ct-alerts">
          {alertItems.map(a => (
            <button key={a.key} className={`ct-alert ${a.cls}`} onClick={() => navigate(a.to)}>
              <strong>{a.n}</strong> {a.label}{a.n !== 1 ? 's' : ''}
            </button>
          ))}
        </div>
      ) : (
        <div className="ct-allclear">✅ All clear — no outstanding waivers, invoices, or messages.</div>
      )}

      {/* Wellness flags reported today */}
      {wellness_flags.length > 0 && (
        <div style={{ margin: '0 0 1.25rem' }}>
          <h2 className="ct-section">🩺 Wellness — flagged today ({wellness_flags.length})</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {wellness_flags.map(w => (
              <button
                key={w.athlete_id}
                onClick={() => navigate(`/athlete-card/${w.athlete_id}`)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10, textAlign: 'left', width: '100%',
                  padding: '0.6rem 0.85rem', borderRadius: 10, cursor: 'pointer',
                  background: '#fff7ed', border: '1px solid #fed7aa', color: '#9a3412',
                }}
                title="Open athlete card"
              >
                <span style={{ fontWeight: 700 }}>{w.name}</span>
                <span style={{ fontSize: '0.85rem', color: '#c2410c' }}>{wellnessReason(w)}</span>
                {w.notes && (
                  <span style={{
                    fontSize: '0.8rem', color: '#7c2d12', fontStyle: 'italic', marginLeft: 'auto',
                    maxWidth: '45%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>“{w.notes}”</span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Daily check-in flags (from post-attendance questions) */}
      {(checkin_flags.length > 0 || checkin_compliance) && (
        <div style={{ margin: '0 0 1.25rem' }}>
          <h2 className="ct-section">🩹 Check-in flags — today ({checkin_flags.length})</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {checkin_flags.map(w => (
              <button
                key={w.athlete_id}
                onClick={() => navigate(`/athlete-card/${w.athlete_id}`)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10, textAlign: 'left', width: '100%', flexWrap: 'wrap',
                  padding: '0.6rem 0.85rem', borderRadius: 10, cursor: 'pointer',
                  background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b',
                }}
                title="Open athlete card"
              >
                <span style={{ fontWeight: 700 }}>{w.name}</span>
                {w.days_flagged > 1 && (
                  <span style={{ fontSize: '0.75rem', fontWeight: 700, background: '#b91c1c', color: '#fff', padding: '1px 7px', borderRadius: 999 }}
                    title={`Flagged ${w.days_flagged} of the last 7 days`}>
                    {w.days_flagged}× this wk
                  </span>
                )}
                <span style={{ fontSize: '0.85rem', color: '#b91c1c' }}>{(w.flags || []).join(' · ')}</span>
              </button>
            ))}
            {checkin_flags.length === 0 && checkin_compliance && checkin_compliance.checked_in > 0 && (
              <div style={{ fontSize: '0.85rem', color: '#16a34a', padding: '0.3rem 0.1rem' }}>
                ✅ No flags — {checkin_compliance.checked_in}/{checkin_compliance.expected} checked in.
              </div>
            )}
            {checkin_compliance && checkin_compliance.missing.length > 0 && (
              <div style={{ fontSize: '0.82rem', color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '0.45rem 0.7rem' }}>
                ⏳ {checkin_compliance.checked_in}/{checkin_compliance.expected} checked in — waiting on: {checkin_compliance.missing.slice(0, 12).join(', ')}{checkin_compliance.missing.length > 12 ? ` +${checkin_compliance.missing.length - 12} more` : ''}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Today's classes */}
      <h2 className="ct-section">📋 Today's Classes{weekday ? ` · ${weekday}` : ''}</h2>
      {classes.length === 0 ? (
        <div className="ct-empty">
          No classes scheduled for you today. Set class times on the <button className="ct-link" onClick={() => navigate('/calendar')}>Schedule</button> page.
        </div>
      ) : (
        <div className="ct-classes">
          {classes.map(c => (
            <div key={c.class_id} className="ct-class">
              <div className="ct-class-main">
                <div className="ct-class-name">{c.class_name}{c.level ? ` · ${c.level}` : ''}</div>
                <div className="ct-class-meta">
                  {fmtTime(c.start_time)}{c.end_time ? `–${fmtTime(c.end_time)}` : ''} · {c.enrolled_count} enrolled
                </div>
              </div>
              <div className="ct-class-right">
                {c.attendance_taken ? (
                  <span className="ct-taken">✓ {c.present_count}/{c.enrolled_count} present</span>
                ) : (
                  <span className="ct-untaken">attendance not taken</span>
                )}
                <button
                  className="ct-take"
                  onClick={() => navigate(`/attendance?class=${c.class_id}&date=${todayStr()}`)}
                >
                  {c.attendance_taken ? 'Edit attendance' : '📝 Take attendance'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Cliff-notes of today's attendance check-in questionnaires */}
      <CheckinSummary />

      {/* Session plans pushed to this coach for today */}
      <CoachSessionAssignments />

      {/* Quick links */}
      <div className="ct-quick">
        <button onClick={() => navigate('/athletes')}>👥 Roster</button>
        <button onClick={() => navigate('/calendar')}>🗓 Schedule</button>
        <button onClick={() => navigate('/communication')}>💬 Announce / Message</button>
        <button onClick={() => navigate('/class-management')}>📚 Classes</button>
      </div>
    </div>
  );
}

export default CoachToday;
